import { getColors } from '../../charts/shared/palettes';
import { JN, LEGEND_SPACING, TITLE_SPACING, fileHeader } from './shared';

export function genBar(chartType, config, t, data) {
  const chartId = chartType.id;
  const getBarSortValue = (row) => {
    if (typeof row?.value === 'number' && Number.isFinite(row.value)) return row.value;
    const numericValues = Object.keys(row || {})
      .filter((key) => key !== 'name' && typeof row[key] === 'number' && Number.isFinite(row[key]))
      .map((key) => row[key]);
    if (numericValues.length === 0) return 0;
    const sum = numericValues.reduce((acc, current) => acc + current, 0);
    return sum / numericValues.length;
  };
  const sortedData = [...data];
  if (config.barOrdering === 'ascending') {
    sortedData.sort((a, b) => getBarSortValue(a) - getBarSortValue(b));
  } else if (config.barOrdering === 'descending') {
    sortedData.sort((a, b) => getBarSortValue(b) - getBarSortValue(a));
  }

  let colors, seriesKeys, labelMap, numSeries;
  if (chartId === 'grouped-bar' || chartId === 'horizontal-grouped-bar') {
    seriesKeys = data._seriesKeys || Array.from({ length: config.numSeries || 3 }, (_, i) => `series${i + 1}`);
    labelMap = data._labelMap || Object.fromEntries(seriesKeys.map((k, i) => [k, `Series ${i + 1}`]));
    numSeries = seriesKeys.length;
    colors = getColors(config, numSeries, t);
  } else if (chartId === 'stacked-bar' || chartId === 'horizontal-stacked-bar') {
    seriesKeys = data._seriesKeys || Array.from({ length: config.numStacks || 3 }, (_, i) => `stack${i + 1}`);
    labelMap = data._labelMap || Object.fromEntries(seriesKeys.map((k, i) => [k, `Stack ${i + 1}`]));
    numSeries = seriesKeys.length;
    colors = getColors(config, numSeries, t).reverse();
  } else {
    colors = getColors(config, data.length, t);
  }

  const showGrid = !!config.showGrid;
  const showValues = !!config.showValues;
  const showLegend = !!config.showLegend;
  const showRef = !!(config.showReferenceLine && config.referenceValue);
  const hasTitle = !!(config.title && String(config.title).trim());
  const labelRot = config.labelRotation || 0;
  const valueDecimals = config.valueDecimals ?? 1;
  const cleanData = Array.from(sortedData);
  const titleStr = hasTitle ? String(config.title).replace(/</g, '&lt;') : '';
  const refVal = showRef ? parseFloat(config.referenceValue) : null;
  const refLabel = config.referenceLabel || 'Reference';

  // Path helpers using string concatenation (avoids backtick escaping in generated file)
  const pathHelpersCode = `
function topRoundedRect(x, y, w, h, r) {
  const radius = Math.min(r, h, w / 2);
  if (radius <= 0 || h <= 0) return 'M' + x + ',' + (y + h) + ' L' + x + ',' + y + ' L' + (x + w) + ',' + y + ' L' + (x + w) + ',' + (y + h) + ' Z';
  return 'M' + x + ',' + (y + h) + ' L' + x + ',' + (y + radius) + ' Q' + x + ',' + y + ' ' + (x + radius) + ',' + y + ' L' + (x + w - radius) + ',' + y + ' Q' + (x + w) + ',' + y + ' ' + (x + w) + ',' + (y + radius) + ' L' + (x + w) + ',' + (y + h) + ' Z';
}

function rightRoundedRect(x, y, w, h, r) {
  const radius = Math.min(r, h / 2, w);
  if (radius <= 0 || w <= 0) return 'M' + x + ',' + y + ' L' + (x + w) + ',' + y + ' L' + (x + w) + ',' + (y + h) + ' L' + x + ',' + (y + h) + ' Z';
  return 'M' + x + ',' + y + ' L' + (x + w - radius) + ',' + y + ' Q' + (x + w) + ',' + y + ' ' + (x + w) + ',' + (y + radius) + ' L' + (x + w) + ',' + (y + h - radius) + ' Q' + (x + w) + ',' + (y + h) + ' ' + (x + w - radius) + ',' + (y + h) + ' L' + x + ',' + (y + h) + ' Z';
}`;

  const inlineLegendCode = `
function InlineLegend({ items, innerWidth }) {
  const itemW = 95;
  const totalW = items.length * itemW;
  const startX = Math.max(0, (innerWidth - totalW) / 2);
  return (
    <g transform="translate(0, -${LEGEND_SPACING.topOffset})">
      {items.map((item, i) => (
        <g key={item.key} transform={'translate(' + (startX + i * itemW) + ', 0)'}>
          <rect x={0} y={-${Math.round(LEGEND_SPACING.rectSize / 2)}} width={${LEGEND_SPACING.rectSize}} height={${LEGEND_SPACING.rectSize}} fill={item.color} />
          <text x={${LEGEND_SPACING.rectTextX}} y={1} fontSize={11} fontFamily={THEME.fontLabel} fill={THEME.legendTextColor}>{item.label}</text>
        </g>
      ))}
    </g>
  );
}`;

  const tickLabelPropsStr = labelRot
    ? `() => ({ fill: THEME.tickColor, fontSize: 12, fontFamily: THEME.fontLabel, textAnchor: 'end', transform: 'rotate(-${labelRot})', dx: '-0.25em', dy: '0.25em' })`
    : `() => ({ fill: THEME.tickColor, fontSize: 12, fontFamily: THEME.fontLabel, textAnchor: 'middle', dy: 10 })`;

  let body = '';

  if (chartId === 'vertical-bar') {
    const vals = cleanData.map(d => d.value).filter(v => v !== undefined);
    const mn = vals.length ? Math.min(...vals) : 0;
    const mx = vals.length ? Math.max(...vals) : 100;
    const yMin = (config.yMin && String(config.yMin).trim() !== '') ? parseFloat(config.yMin) : Math.floor(mn * 0.9);
    const yMax = (config.yMax && String(config.yMax).trim() !== '') ? parseFloat(config.yMax) : Math.ceil(mx * 1.05);
    const marginTop = hasTitle ? 40 : 20;
    const marginBottom = labelRot ? 80 : 60;
    const gridImport = showGrid ? `import { GridRows } from '@visx/grid';\n` : '';
    const gridBlock = showGrid ? `          <GridRows scale={yScale} width={innerWidth} strokeDasharray="3 3" stroke={THEME.gridColor} strokeOpacity={THEME.gridAlpha} />\n` : '';
    const refBlock = showRef ? `          <line x1={0} y1={yScale(${refVal})} x2={innerWidth} y2={yScale(${refVal})} stroke={THEME.referenceLineColor} strokeDasharray="5 5" strokeWidth={2} />\n          <text x={innerWidth} y={yScale(${refVal}) - 4} textAnchor="end" fill={THEME.referenceLineColor} fontSize={12} fontFamily={THEME.fontLabel}>${refLabel}</text>\n` : '';
    const valBlock = showValues ? `\n                <text x={x + bw / 2} y={y - 5} textAnchor="middle" fontSize={11} fontWeight={500} fontFamily={THEME.fontNumber} fill={THEME.foreground}>{d.value.toFixed(${valueDecimals})}</text>` : '';
    const titleBlock = hasTitle ? `\n      <div style={{ position: 'absolute', top: ${TITLE_SPACING.topInset}, left: 70, fontSize: 18, fontWeight: 400, color: THEME.titleColor, fontFamily: THEME.fontHeading }}>${titleStr}</div>` : '';

    body = `import React, { useMemo } from 'react';
import { scaleBand, scaleLinear } from '@visx/scale';
import { Group } from '@visx/group';
${gridImport}import { AxisLeft, AxisBottom } from '@visx/axis';

const DATA = ${JN(cleanData)};
const COLORS = ${JN(colors)};
const THEME = ${JN(t)};
const Y_DOMAIN = [${yMin}, ${yMax}];
${pathHelpersCode}
export default function Chart({ width = 720, height = 480 }) {
  const margin = { top: ${marginTop}, right: 80, bottom: ${marginBottom}, left: 70 };
  const innerWidth = Math.max(0, width - margin.left - margin.right);
  const innerHeight = Math.max(0, height - margin.top - margin.bottom);

  const xScale = useMemo(() => scaleBand({ domain: DATA.map(d => d.name), range: [0, innerWidth], padding: 0.35 }), [innerWidth]);
  const yScale = useMemo(() => scaleLinear({ domain: Y_DOMAIN, range: [innerHeight, 0] }), [innerHeight]);

  // Proportional bar width (see computeBarWidth in chartLayout.js)
  const _bwCap = innerWidth * Math.min(0.065, 0.7 / DATA.length);
  const bw = Math.max(2, Math.min(xScale.bandwidth(), _bwCap));
  const bOff = (xScale.bandwidth() - bw) / 2;
  const effectiveRadius = Math.min(THEME.barRadius, bw * 0.15);

  return (
    <div style={{ width, height, position: 'relative', backgroundColor: THEME.background }}>${titleBlock}
      <svg width={width} height={height}>
        <Group left={margin.left} top={margin.top}>
${gridBlock}          {DATA.map((d, i) => {
            const x = (xScale(d.name) ?? 0) + bOff;
            const y = yScale(d.value);
            const barH = innerHeight - y;
            if (barH <= 0) return null;
            return (
              <g key={d.name}>
                <path d={topRoundedRect(x, y, bw, barH, effectiveRadius)} fill={COLORS[i]} />${valBlock}
              </g>
            );
          })}
${refBlock}          <AxisLeft scale={yScale} stroke={THEME.axisColor} strokeWidth={THEME.spineWidth} tickStroke={THEME.axisColor} tickLabelProps={() => ({ fill: THEME.tickColor, fontSize: 12, fontFamily: THEME.fontNumber, textAnchor: 'end', dx: -4, dy: '0.33em' })} />
          <AxisBottom scale={xScale} top={innerHeight} stroke={THEME.axisColor} strokeWidth={THEME.spineWidth} tickStroke={THEME.axisColor} tickLabelProps={${tickLabelPropsStr}} />
          <text x={-innerHeight / 2} y={-(margin.left - 20)} transform="rotate(-90)" textAnchor="middle" fontSize={14} fontFamily={THEME.fontLabel} fill={THEME.labelColor}>${config.ylabel || ''}</text>
          <text x={innerWidth / 2} y={innerHeight + margin.bottom - 15} textAnchor="middle" fontSize={14} fontFamily={THEME.fontLabel} fill={THEME.labelColor}>${config.xlabel || ''}</text>
        </Group>
      </svg>
    </div>
  );
}`;

  } else if (chartId === 'horizontal-bar') {
    const vals = cleanData.map(d => d.value);
    const mx = vals.length ? Math.max(...vals) : 100;
    const xMin = (config.xMin && String(config.xMin).trim() !== '') ? parseFloat(config.xMin) : 0;
    const xMax = (config.xMax && String(config.xMax).trim() !== '') ? parseFloat(config.xMax) : Math.ceil(mx * 1.1);
    const marginTop = hasTitle ? 40 : 20;
    const gridImport = showGrid ? `import { GridColumns } from '@visx/grid';\n` : '';
    const gridBlock = showGrid ? `          <GridColumns scale={xScale} height={innerHeight} strokeDasharray="3 3" stroke={THEME.gridColor} strokeOpacity={THEME.gridAlpha} />\n` : '';
    const refBlock = showRef ? `          <line x1={xScale(${refVal})} y1={0} x2={xScale(${refVal})} y2={innerHeight} stroke={THEME.referenceLineColor} strokeDasharray="5 5" strokeWidth={2} />\n          <text x={xScale(${refVal}) + 4} y={8} textAnchor="start" fill={THEME.referenceLineColor} fontSize={12} fontFamily={THEME.fontLabel}>${refLabel}</text>\n` : '';
    const valBlock = showValues ? `\n                <text x={barW + 5} y={y + bw / 2} dominantBaseline="middle" fontSize={11} fontWeight={500} fontFamily={THEME.fontNumber} fill={THEME.foreground}>{d.value.toFixed(${valueDecimals})}</text>` : '';
    const titleBlock = hasTitle ? `\n      <div style={{ position: 'absolute', top: ${TITLE_SPACING.topInset}, left: 100, fontSize: 18, fontWeight: 400, color: THEME.titleColor, fontFamily: THEME.fontHeading }}>${titleStr}</div>` : '';

    body = `import React, { useMemo } from 'react';
import { scaleBand, scaleLinear } from '@visx/scale';
import { Group } from '@visx/group';
${gridImport}import { AxisLeft, AxisBottom } from '@visx/axis';

const DATA = ${JN(cleanData)};
const COLORS = ${JN(colors)};
const THEME = ${JN(t)};
const X_DOMAIN = [${xMin}, ${xMax}];
${pathHelpersCode}
export default function Chart({ width = 720, height = 480 }) {
  const margin = { top: ${marginTop}, right: 60, bottom: 50, left: 100 };
  const innerWidth = Math.max(0, width - margin.left - margin.right);
  const innerHeight = Math.max(0, height - margin.top - margin.bottom);

  const yScale = useMemo(() => scaleBand({ domain: DATA.map(d => d.name), range: [0, innerHeight], padding: 0.35 }), [innerHeight]);
  const xScale = useMemo(() => scaleLinear({ domain: X_DOMAIN, range: [0, innerWidth] }), [innerWidth]);

  // Proportional bar width (see computeBarWidth in chartLayout.js)
  const _bwCap = innerHeight * Math.min(0.085, 0.7 / DATA.length);
  const bw = Math.max(2, Math.min(yScale.bandwidth(), _bwCap));
  const bOff = (yScale.bandwidth() - bw) / 2;
  const effectiveRadius = Math.min(THEME.barRadius, bw * 0.15);

  return (
    <div style={{ width, height, position: 'relative', backgroundColor: THEME.background }}>${titleBlock}
      <svg width={width} height={height}>
        <Group left={margin.left} top={margin.top}>
${gridBlock}          {DATA.map((d, i) => {
            const y = (yScale(d.name) ?? 0) + bOff;
            const barW = xScale(d.value);
            if (barW <= 0) return null;
            return (
              <g key={d.name}>
                <path d={rightRoundedRect(0, y, barW, bw, effectiveRadius)} fill={COLORS[i]} />${valBlock}
              </g>
            );
          })}
${refBlock}          <AxisLeft scale={yScale} numTicks={DATA.length} stroke={THEME.axisColor} strokeWidth={THEME.spineWidth} tickStroke={THEME.axisColor} tickLabelProps={() => ({ fill: THEME.tickColor, fontFamily: THEME.fontLabel, fontSize: Math.max(9, Math.min(12, Math.floor(innerHeight / DATA.length / 1.35))), textAnchor: 'end', dx: -4, dy: yScale.bandwidth() / 2 })} />
          <AxisBottom scale={xScale} top={innerHeight} stroke={THEME.axisColor} strokeWidth={THEME.spineWidth} tickStroke={THEME.axisColor} tickLabelProps={() => ({ fill: THEME.tickColor, fontSize: 12, fontFamily: THEME.fontNumber, textAnchor: 'middle', dy: 10 })} />
          <text x={-innerHeight / 2} y={-(margin.left - 20)} transform="rotate(-90)" textAnchor="middle" fontSize={14} fontFamily={THEME.fontLabel} fill={THEME.labelColor}>${config.ylabel || ''}</text>
          <text x={innerWidth / 2} y={innerHeight + margin.bottom - 10} textAnchor="middle" fontSize={14} fontFamily={THEME.fontLabel} fill={THEME.labelColor}>${config.xlabel || ''}</text>
        </Group>
      </svg>
    </div>
  );
}`;

  } else if (chartId === 'horizontal-grouped-bar') {
    const allValues = cleanData.flatMap(d => seriesKeys.map(k => d[k] || 0));
    const mx = allValues.length ? Math.max(...allValues) : 100;
    const xMin = (config.xMin && String(config.xMin).trim() !== '') ? parseFloat(config.xMin) : 0;
    const xMax = (config.xMax && String(config.xMax).trim() !== '') ? parseFloat(config.xMax) : Math.ceil(mx * 1.1);
    const marginTop = hasTitle ? 60 : 40;
    const legendItems = seriesKeys.map((k, i) => ({ key: k, label: labelMap ? (labelMap[k] || k) : `Series ${i + 1}`, color: colors[i] }));
    const gridImport = showGrid ? `import { GridColumns } from '@visx/grid';\n` : '';
    const gridBlock = showGrid ? `          <GridColumns scale={xScale} height={innerHeight} strokeDasharray="3 3" stroke={THEME.gridColor} strokeOpacity={THEME.gridAlpha} />\n` : '';
    const legendBlock = showLegend ? `          <InlineLegend items={LEGEND_ITEMS} innerWidth={innerWidth} />\n` : '';
    const refBlock = showRef ? `          <line x1={xScale(${refVal})} y1={0} x2={xScale(${refVal})} y2={innerHeight} stroke={THEME.referenceLineColor} strokeDasharray="5 5" strokeWidth={2} />\n          <text x={xScale(${refVal}) + 4} y={8} textAnchor="start" fill={THEME.referenceLineColor} fontSize={12} fontFamily={THEME.fontLabel}>${refLabel}</text>\n` : '';
    const valBlock = showValues ? `\n                      <text x={barW + 5} y={y + bw / 2} dominantBaseline="middle" fontSize={11} fontWeight={500} fontFamily={THEME.fontNumber} fill={THEME.foreground}>{val.toFixed(${valueDecimals})}</text>` : '';
    const titleBlock = hasTitle ? `\n      <div style={{ position: 'absolute', top: ${TITLE_SPACING.topInset}, left: 100, fontSize: 18, fontWeight: 400, color: THEME.titleColor, fontFamily: THEME.fontHeading }}>${titleStr}</div>` : '';

    body = `import React, { useMemo } from 'react';
import { scaleBand, scaleLinear } from '@visx/scale';
import { Group } from '@visx/group';
${gridImport}import { AxisLeft, AxisBottom } from '@visx/axis';

const DATA = ${JN(cleanData)};
const SERIES_KEYS = ${JN(seriesKeys)};
const SERIES_COLORS = ${JN(colors)};
const LEGEND_ITEMS = ${JN(legendItems)};
const THEME = ${JN(t)};
const X_DOMAIN = [${xMin}, ${xMax}];
const NUM_SERIES = ${numSeries};
${pathHelpersCode}
${inlineLegendCode}
export default function Chart({ width = 720, height = 480 }) {
  const margin = { top: ${marginTop}, right: 80, bottom: 60, left: 120 };
  const innerWidth = Math.max(0, width - margin.left - margin.right);
  const innerHeight = Math.max(0, height - margin.top - margin.bottom);
  const yOuter = useMemo(() => scaleBand({ domain: DATA.map(d => d.name), range: [0, innerHeight], padding: 0.2 }), [innerHeight]);
  const yInner = useMemo(() => scaleBand({ domain: SERIES_KEYS, range: [0, yOuter.bandwidth()], padding: 0.05 }), [yOuter]);
  const xScale = useMemo(() => scaleLinear({ domain: X_DOMAIN, range: [0, innerWidth] }), [innerWidth]);
  const _bwCap = innerHeight * Math.min(Math.max(0.012, 0.15 / NUM_SERIES), 0.7 / (DATA.length * NUM_SERIES));
  const bw = Math.max(2, Math.min(yInner.bandwidth(), _bwCap));
  const bOff = (yInner.bandwidth() - bw) / 2;
  const effectiveRadius = Math.min(THEME.barRadius, bw * 0.15);
  return (
    <div style={{ width, height, position: 'relative', backgroundColor: THEME.background }}>${titleBlock}
      <svg width={width} height={height}>
        <Group left={margin.left} top={margin.top}>
${legendBlock}${gridBlock}          {DATA.map((d) => {
            const groupY = yOuter(d.name) ?? 0;
            return (
              <g key={d.name}>
                {SERIES_KEYS.map((key, sIdx) => {
                  const val = d[key] || 0;
                  const y = groupY + (yInner(key) ?? 0) + bOff;
                  const barW = xScale(val) - xScale(0);
                  if (barW <= 0) return null;
                  return <g key={key}><path d={rightRoundedRect(0, y, barW, bw, effectiveRadius)} fill={SERIES_COLORS[sIdx]} />${valBlock}</g>;
                })}
              </g>
            );
          })}
${refBlock}          <AxisLeft scale={yOuter} stroke={THEME.axisColor} strokeWidth={THEME.spineWidth} tickStroke={THEME.axisColor} tickLabelProps={() => ({ fill: THEME.tickColor, fontSize: 12, fontFamily: THEME.fontLabel, textAnchor: 'end', dx: -4, dy: '0.33em' })} />
          <AxisBottom scale={xScale} top={innerHeight} stroke={THEME.axisColor} strokeWidth={THEME.spineWidth} tickStroke={THEME.axisColor} tickLabelProps={() => ({ fill: THEME.tickColor, fontSize: 12, fontFamily: THEME.fontNumber, textAnchor: 'middle', dy: 10 })} />
          <text x={-innerHeight / 2} y={-(margin.left - 20)} transform="rotate(-90)" textAnchor="middle" fontSize={14} fontFamily={THEME.fontLabel} fill={THEME.labelColor}>${config.ylabel || ''}</text>
          <text x={innerWidth / 2} y={innerHeight + margin.bottom - 10} textAnchor="middle" fontSize={14} fontFamily={THEME.fontLabel} fill={THEME.labelColor}>${config.xlabel || ''}</text>
        </Group>
      </svg>
    </div>
  );
}`;

  } else if (chartId === 'horizontal-stacked-bar') {
    const totals = cleanData.map(d => seriesKeys.reduce((sum, k) => sum + (d[k] || 0), 0));
    const xMin = (config.xMin && String(config.xMin).trim() !== '') ? parseFloat(config.xMin) : 0;
    const xMax = (config.xMax && String(config.xMax).trim() !== '') ? parseFloat(config.xMax) : Math.ceil(Math.max(...totals, 0) * 1.05);
    const marginTop = hasTitle ? 60 : 40;
    const legendItems = seriesKeys.map((k, i) => ({ key: k, label: labelMap ? (labelMap[k] || k) : `Stack ${i + 1}`, color: colors[i] }));
    const gridImport = showGrid ? `import { GridColumns } from '@visx/grid';\n` : '';
    const gridBlock = showGrid ? `          <GridColumns scale={xScale} height={innerHeight} strokeDasharray="3 3" stroke={THEME.gridColor} strokeOpacity={THEME.gridAlpha} />\n` : '';
    const legendBlock = showLegend ? `          <InlineLegend items={LEGEND_ITEMS} innerWidth={innerWidth} />\n` : '';
    const refBlock = showRef ? `          <line x1={xScale(${refVal})} y1={0} x2={xScale(${refVal})} y2={innerHeight} stroke={THEME.referenceLineColor} strokeDasharray="5 5" strokeWidth={2} />\n          <text x={xScale(${refVal}) + 4} y={8} textAnchor="start" fill={THEME.referenceLineColor} fontSize={12} fontFamily={THEME.fontLabel}>${refLabel}</text>\n` : '';
    const titleBlock = hasTitle ? `\n      <div style={{ position: 'absolute', top: ${TITLE_SPACING.topInset}, left: 100, fontSize: 18, fontWeight: 400, color: THEME.titleColor, fontFamily: THEME.fontHeading }}>${titleStr}</div>` : '';
    const valBlock = showValues ? `                {item.segments.map((seg, sIdx) => {
                  const segX2 = xScale(seg.from);
                  const segW2 = xScale(seg.to) - xScale(seg.from);
                  if (segW2 < 18) return null;
                  return <text key={'val-' + seg.key} x={segX2 + segW2 / 2} y={y + bw / 2} textAnchor="middle" dominantBaseline="middle" fontSize={10} fontWeight={500} fontFamily={THEME.fontNumber} fill={THEME.foreground}>{seg.val.toFixed(${valueDecimals})}</text>;
                })}` : '';

    body = `import React, { useMemo } from 'react';
import { scaleBand, scaleLinear } from '@visx/scale';
import { Group } from '@visx/group';
${gridImport}import { AxisLeft, AxisBottom } from '@visx/axis';

const DATA = ${JN(cleanData)};
const STACK_KEYS = ${JN(seriesKeys)};
const STACK_COLORS = ${JN(colors)};
const LEGEND_ITEMS = ${JN(legendItems)};
const THEME = ${JN(t)};
const X_DOMAIN = [${xMin}, ${xMax}];
const NUM_STACKS = ${numSeries};
${pathHelpersCode}
${inlineLegendCode}
export default function Chart({ width = 720, height = 480 }) {
  const margin = { top: ${marginTop}, right: 80, bottom: 60, left: 120 };
  const innerWidth = Math.max(0, width - margin.left - margin.right);
  const innerHeight = Math.max(0, height - margin.top - margin.bottom);
  const yScale = useMemo(() => scaleBand({ domain: DATA.map(d => d.name), range: [0, innerHeight], padding: 0.2 }), [innerHeight]);
  const xScale = useMemo(() => scaleLinear({ domain: X_DOMAIN, range: [0, innerWidth] }), [innerWidth]);
  const _bwCap = innerHeight * Math.min(0.08, 0.7 / DATA.length);
  const bw = Math.max(2, Math.min(yScale.bandwidth(), _bwCap));
  const bOff = (yScale.bandwidth() - bw) / 2;
  const effectiveRadius = Math.min(THEME.barRadius, bw * 0.15);
  const stackedData = useMemo(() => DATA.map(d => {
    let cumulative = 0;
    return { name: d.name, segments: STACK_KEYS.map((key, sIdx) => {
      const val = d[key] || 0;
      const from = cumulative;
      const to = cumulative + val;
      cumulative = to;
      return { key, val, from, to, isTop: sIdx === NUM_STACKS - 1 };
    }) };
  }), []);
  return (
    <div style={{ width, height, position: 'relative', backgroundColor: THEME.background }}>${titleBlock}
      <svg width={width} height={height}>
        <Group left={margin.left} top={margin.top}>
${legendBlock}${gridBlock}          {stackedData.map((item) => {
            const y = (yScale(item.name) ?? 0) + bOff;
            return (
              <g key={item.name}>
                {item.segments.map((seg, sIdx) => {
                  const segX = xScale(seg.from);
                  const segW = xScale(seg.to) - xScale(seg.from);
                  if (segW <= 0) return null;
                  const pathD = seg.isTop ? rightRoundedRect(segX, y, segW, bw, effectiveRadius) : 'M' + segX + ',' + y + ' h' + segW + ' v' + bw + ' h' + (-segW) + 'Z';
                  return <path key={seg.key} d={pathD} fill={STACK_COLORS[sIdx]} />;
                })}
${valBlock}              </g>
            );
          })}
${refBlock}          <AxisLeft scale={yScale} stroke={THEME.axisColor} strokeWidth={THEME.spineWidth} tickStroke={THEME.axisColor} tickLabelProps={() => ({ fill: THEME.tickColor, fontSize: 12, fontFamily: THEME.fontLabel, textAnchor: 'end', dx: -4, dy: '0.33em' })} />
          <AxisBottom scale={xScale} top={innerHeight} stroke={THEME.axisColor} strokeWidth={THEME.spineWidth} tickStroke={THEME.axisColor} tickLabelProps={() => ({ fill: THEME.tickColor, fontSize: 12, fontFamily: THEME.fontNumber, textAnchor: 'middle', dy: 10 })} />
          <text x={-innerHeight / 2} y={-(margin.left - 20)} transform="rotate(-90)" textAnchor="middle" fontSize={14} fontFamily={THEME.fontLabel} fill={THEME.labelColor}>${config.ylabel || ''}</text>
          <text x={innerWidth / 2} y={innerHeight + margin.bottom - 10} textAnchor="middle" fontSize={14} fontFamily={THEME.fontLabel} fill={THEME.labelColor}>${config.xlabel || ''}</text>
        </Group>
      </svg>
    </div>
  );
}`;

  } else if (chartId === 'grouped-bar') {
    const allValues = cleanData.flatMap(d => seriesKeys.map(k => d[k] || 0));
    const mn = allValues.length ? Math.min(...allValues) : 0;
    const mx = allValues.length ? Math.max(...allValues) : 100;
    const yMin = (config.yMin && String(config.yMin).trim() !== '') ? parseFloat(config.yMin) : Math.floor(mn * 0.9);
    const yMax = (config.yMax && String(config.yMax).trim() !== '') ? parseFloat(config.yMax) : Math.ceil(mx * 1.1);
    const marginTop = hasTitle ? 60 : 40;
    const marginBottom = labelRot ? 80 : 60;
    const legendItems = seriesKeys.map((k, i) => ({ key: k, label: labelMap ? (labelMap[k] || k) : `Series ${i + 1}`, color: colors[i] }));
    const gridImport = showGrid ? `import { GridRows } from '@visx/grid';\n` : '';
    const gridBlock = showGrid ? `          <GridRows scale={yScale} width={innerWidth} strokeDasharray="3 3" stroke={THEME.gridColor} strokeOpacity={THEME.gridAlpha} />\n` : '';
    const legendBlock = showLegend ? `          <InlineLegend items={LEGEND_ITEMS} innerWidth={innerWidth} />\n` : '';
    const refBlock = showRef ? `          <line x1={0} y1={yScale(${refVal})} x2={innerWidth} y2={yScale(${refVal})} stroke={THEME.referenceLineColor} strokeDasharray="5 5" strokeWidth={2} />\n          <text x={innerWidth} y={yScale(${refVal}) - 4} textAnchor="end" fill={THEME.referenceLineColor} fontSize={12} fontFamily={THEME.fontLabel}>${refLabel}</text>\n` : '';
    const valBlock = showValues ? `\n                      <text x={x + bw / 2} y={y - 4} textAnchor="middle" fontSize={9} fontWeight={500} fontFamily={THEME.fontNumber} fill={THEME.foreground}>{val.toFixed(${valueDecimals})}</text>` : '';
    const titleBlock = hasTitle ? `\n      <div style={{ position: 'absolute', top: ${TITLE_SPACING.topInset}, left: 70, fontSize: 18, fontWeight: 400, color: THEME.titleColor, fontFamily: THEME.fontHeading }}>${titleStr}</div>` : '';

    body = `import React, { useMemo } from 'react';
import { scaleBand, scaleLinear } from '@visx/scale';
import { Group } from '@visx/group';
${gridImport}import { AxisLeft, AxisBottom } from '@visx/axis';

const DATA = ${JN(cleanData)};
const SERIES_KEYS = ${JN(seriesKeys)};
const SERIES_COLORS = ${JN(colors)};
const LEGEND_ITEMS = ${JN(legendItems)};
const THEME = ${JN(t)};
const Y_DOMAIN = [${yMin}, ${yMax}];
const NUM_SERIES = ${numSeries};
${pathHelpersCode}
${inlineLegendCode}
export default function Chart({ width = 720, height = 480 }) {
  const margin = { top: ${marginTop}, right: 80, bottom: ${marginBottom}, left: 70 };
  const innerWidth = Math.max(0, width - margin.left - margin.right);
  const innerHeight = Math.max(0, height - margin.top - margin.bottom);

  const xOuter = useMemo(() => scaleBand({ domain: DATA.map(d => d.name), range: [0, innerWidth], padding: 0.2 }), [innerWidth]);
  const xInner = useMemo(() => scaleBand({ domain: SERIES_KEYS, range: [0, xOuter.bandwidth()], padding: 0.05 }), [xOuter]);
  const yScale = useMemo(() => scaleLinear({ domain: Y_DOMAIN, range: [innerHeight, 0] }), [innerHeight]);

  // Proportional bar width (see computeBarWidth in chartLayout.js)
  const _bwCap = innerWidth * Math.min(Math.max(0.012, 0.15 / NUM_SERIES), 0.7 / (DATA.length * NUM_SERIES));
  const bw = Math.max(2, Math.min(xInner.bandwidth(), _bwCap));
  const bOff = (xInner.bandwidth() - bw) / 2;
  const effectiveRadius = Math.min(THEME.barRadius, bw * 0.15);

  return (
    <div style={{ width, height, position: 'relative', backgroundColor: THEME.background }}>${titleBlock}
      <svg width={width} height={height}>
        <Group left={margin.left} top={margin.top}>
${legendBlock}${gridBlock}          {DATA.map((d) => {
            const groupX = xOuter(d.name) ?? 0;
            return (
              <g key={d.name}>
                {SERIES_KEYS.map((key, sIdx) => {
                  const val = d[key] || 0;
                  const x = groupX + (xInner(key) ?? 0) + bOff;
                  const y = yScale(val);
                  const barH = innerHeight - y;
                  if (barH <= 0) return null;
                  return (
                    <g key={key}>
                      <path d={topRoundedRect(x, y, bw, barH, effectiveRadius)} fill={SERIES_COLORS[sIdx]} />${valBlock}
                    </g>
                  );
                })}
              </g>
            );
          })}
${refBlock}          <AxisLeft scale={yScale} stroke={THEME.axisColor} strokeWidth={THEME.spineWidth} tickStroke={THEME.axisColor} tickLabelProps={() => ({ fill: THEME.tickColor, fontSize: 12, fontFamily: THEME.fontNumber, textAnchor: 'end', dx: -4, dy: '0.33em' })} />
          <AxisBottom scale={xOuter} top={innerHeight} stroke={THEME.axisColor} strokeWidth={THEME.spineWidth} tickStroke={THEME.axisColor} tickLabelProps={${tickLabelPropsStr}} />
          <text x={-innerHeight / 2} y={-(margin.left - 20)} transform="rotate(-90)" textAnchor="middle" fontSize={14} fontFamily={THEME.fontLabel} fill={THEME.labelColor}>${config.ylabel || ''}</text>
          <text x={innerWidth / 2} y={innerHeight + margin.bottom - 15} textAnchor="middle" fontSize={14} fontFamily={THEME.fontLabel} fill={THEME.labelColor}>${config.xlabel || ''}</text>
        </Group>
      </svg>
    </div>
  );
}`;

  } else if (chartId === 'stacked-bar') {
    const totals = cleanData.map(d => seriesKeys.reduce((sum, k) => sum + (d[k] || 0), 0));
    const yMin = (config.yMin && String(config.yMin).trim() !== '') ? parseFloat(config.yMin) : 0;
    const yMax = (config.yMax && String(config.yMax).trim() !== '') ? parseFloat(config.yMax) : Math.ceil(Math.max(...totals, 0) * 1.05);
    const marginTop = hasTitle ? 60 : 40;
    const marginBottom = labelRot ? 80 : 60;
    const legendItems = seriesKeys.map((k, i) => ({ key: k, label: labelMap ? (labelMap[k] || k) : `Stack ${i + 1}`, color: colors[i] }));
    const gridImport = showGrid ? `import { GridRows } from '@visx/grid';\n` : '';
    const gridBlock = showGrid ? `          <GridRows scale={yScale} width={innerWidth} strokeDasharray="3 3" stroke={THEME.gridColor} strokeOpacity={THEME.gridAlpha} />\n` : '';
    const legendBlock = showLegend ? `          <InlineLegend items={LEGEND_ITEMS} innerWidth={innerWidth} />\n` : '';
    const refBlock = showRef ? `          <line x1={0} y1={yScale(${refVal})} x2={innerWidth} y2={yScale(${refVal})} stroke={THEME.referenceLineColor} strokeDasharray="5 5" strokeWidth={2} />\n          <text x={innerWidth} y={yScale(${refVal}) - 4} textAnchor="end" fill={THEME.referenceLineColor} fontSize={12} fontFamily={THEME.fontLabel}>${refLabel}</text>\n` : '';
    const titleBlock = hasTitle ? `\n      <div style={{ position: 'absolute', top: ${TITLE_SPACING.topInset}, left: 70, fontSize: 18, fontWeight: 400, color: THEME.titleColor, fontFamily: THEME.fontHeading }}>${titleStr}</div>` : '';
    const valBlock = showValues ? `                {item.segments.map((seg, sIdx) => {
                  const segY2 = yScale(seg.to);
                  const segH2 = yScale(seg.from) - yScale(seg.to);
                  if (segH2 < 14) return null;
                  return (
                    <text key={'val-' + seg.key} x={x + bw / 2} y={segY2 + segH2 / 2} textAnchor="middle" dominantBaseline="middle" fontSize={10} fontWeight={500} fontFamily={THEME.fontNumber} fill={sIdx === NUM_STACKS - 1 ? THEME.foreground : '#ffffff'}>
                      {seg.val.toFixed(${valueDecimals})}
                    </text>
                  );
                })}` : '';

    body = `import React, { useMemo } from 'react';
import { scaleBand, scaleLinear } from '@visx/scale';
import { Group } from '@visx/group';
${gridImport}import { AxisLeft, AxisBottom } from '@visx/axis';

const DATA = ${JN(cleanData)};
const STACK_KEYS = ${JN(seriesKeys)};
const STACK_COLORS = ${JN(colors)};
const LEGEND_ITEMS = ${JN(legendItems)};
const THEME = ${JN(t)};
const Y_DOMAIN = [${yMin}, ${yMax}];
const NUM_STACKS = ${numSeries};
${pathHelpersCode}
${inlineLegendCode}
export default function Chart({ width = 720, height = 480 }) {
  const margin = { top: ${marginTop}, right: 80, bottom: ${marginBottom}, left: 70 };
  const innerWidth = Math.max(0, width - margin.left - margin.right);
  const innerHeight = Math.max(0, height - margin.top - margin.bottom);

  const xScale = useMemo(() => scaleBand({ domain: DATA.map(d => d.name), range: [0, innerWidth], padding: 0.2 }), [innerWidth]);
  const yScale = useMemo(() => scaleLinear({ domain: Y_DOMAIN, range: [innerHeight, 0] }), [innerHeight]);

  // Proportional bar width (see computeBarWidth in chartLayout.js)
  const _bwCap = innerWidth * Math.min(0.08, 0.7 / DATA.length);
  const bw = Math.max(2, Math.min(xScale.bandwidth(), _bwCap));
  const bOff = (xScale.bandwidth() - bw) / 2;
  const effectiveRadius = Math.min(THEME.barRadius, bw * 0.15);

  const stackedData = useMemo(() => DATA.map(d => {
    let cumulative = Y_DOMAIN[0];
    return {
      name: d.name,
      segments: STACK_KEYS.map((key, sIdx) => {
        const val = d[key] || 0;
        const from = cumulative;
        const to = cumulative + val;
        cumulative = to;
        return { key, val, from, to, isTop: sIdx === NUM_STACKS - 1 };
      }),
    };
  }), []);

  return (
    <div style={{ width, height, position: 'relative', backgroundColor: THEME.background }}>${titleBlock}
      <svg width={width} height={height}>
        <Group left={margin.left} top={margin.top}>
${legendBlock}${gridBlock}          {stackedData.map((item) => {
            const x = (xScale(item.name) ?? 0) + bOff;
            return (
              <g key={item.name}>
                {item.segments.map((seg, sIdx) => {
                  const segY = yScale(seg.to);
                  const segH = yScale(seg.from) - yScale(seg.to);
                  if (segH <= 0) return null;
                  const pathD = seg.isTop
                    ? topRoundedRect(x, segY, bw, segH, effectiveRadius)
                    : 'M' + x + ',' + segY + ' h' + bw + ' v' + segH + ' h' + (-bw) + 'Z';
                  return <path key={seg.key} d={pathD} fill={STACK_COLORS[sIdx]} />;
                })}
${valBlock}              </g>
            );
          })}
${refBlock}          <AxisLeft scale={yScale} stroke={THEME.axisColor} strokeWidth={THEME.spineWidth} tickStroke={THEME.axisColor} tickLabelProps={() => ({ fill: THEME.tickColor, fontSize: 12, fontFamily: THEME.fontNumber, textAnchor: 'end', dx: -4, dy: '0.33em' })} />
          <AxisBottom scale={xScale} top={innerHeight} stroke={THEME.axisColor} strokeWidth={THEME.spineWidth} tickStroke={THEME.axisColor} tickLabelProps={${tickLabelPropsStr}} />
          <text x={-innerHeight / 2} y={-(margin.left - 20)} transform="rotate(-90)" textAnchor="middle" fontSize={14} fontFamily={THEME.fontLabel} fill={THEME.labelColor}>${config.ylabel || ''}</text>
          <text x={innerWidth / 2} y={innerHeight + margin.bottom - 15} textAnchor="middle" fontSize={14} fontFamily={THEME.fontLabel} fill={THEME.labelColor}>${config.xlabel || ''}</text>
        </Group>
      </svg>
    </div>
  );
}`;
  }

  return fileHeader(['@visx/scale', '@visx/group', '@visx/grid', '@visx/axis'], chartId) + body;
}
